import React, { useEffect, useMemo, useState } from "react";
import { SupportingDocumentsContainer } from "./primitives";
import UploadSection from "./components/UploadSection";
import DownloadSection from "./components/DownloadSection";
import PaymentSection from "./components/PaymentSection";
import { useSupportingDocuments, useAutoSubmit, useFileDownloads } from "./hooks/useSupportingDocuments";
import { mapDbFilesToConfigs } from "./utils/fileHelpers";
import { DEFAULT_DOWNLOAD_FILES } from "./config/fileTypes";
import {
  GetRegistrationFiles,
  getReRegistrationService,
  GetFileTypeMapping
} from "../../../services/ReRegistrationService";

function SupportingDocuments({
  regId,
  registrationId,
  files,
  downloads = [],
  onSubmitSuccess,
  depositAmount,
  studentNumber = "",
  showPaymentMethods = true,
  setReloadFiles,
  setRegistration,
  activeTab,
  onModuleSummaryChange
}) {
  const [hasFetchedRegistration, setHasFetchedRegistration] = useState(false);
  const [fileTypeMappings, setFileTypeMappings] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  useEffect(() => {
    GetFileTypeMapping().then((res) => {
      setFileTypeMappings(res?.data);
  });
  }, []);
  // fetch re-registration data when tab becomes active and studentNewRegId is missing
  useEffect(() => {
    if (activeTab !== "supporting_documents") {
      setHasFetchedRegistration(false);
      return;
    }

    // Fetch immediately if studentNewRegId is missing
    if (!regId && registrationId && setRegistration && !hasFetchedRegistration) {
      setHasFetchedRegistration(true);
      
      getReRegistrationService(registrationId)
        .then((res) => {
          if (res?.data) {
            const responseData = res.data.responseData || res.data;
            const registrationData = responseData.registration || responseData || {};
            
            // Update registration with studentNewRegId
            setRegistration((prev) => ({
              ...prev,
              ...registrationData,
              studentNewRegId: registrationData.studentNewRegId || 
                              registrationData.student_new_reg_id || 
                              responseData.studentNewRegId ||
                              responseData.student_new_reg_id ||
                              prev?.studentNewRegId
            }));
          }
        })
        .catch((error) => {
          console.error("Error fetching re-registration data:", error);
          setHasFetchedRegistration(false);
        });
    }
  }, [activeTab, regId, registrationId, setRegistration, hasFetchedRegistration]);

  // Merge default required files with API files, avoiding duplicates by file_id
  const mergedFiles = useMemo(() => {
    const apiFiles = files || [];
    return [...apiFiles];
  }, [files]);

  // Map DB rows to fileConfig objects that FileUploadRow expects
  useEffect(() => {
    if (fileTypeMappings?.length && mergedFiles?.length) {
      setUploadFiles(mapDbFilesToConfigs(mergedFiles, fileTypeMappings));
    }else{
      setUploadFiles([]);
    }
  }, [mergedFiles, fileTypeMappings]);
  // Fetch downloads from API
  const { downloadFiles: apiDownloadFiles, loading: downloadsLoading } = useFileDownloads();
  
  // Use API downloads if available, otherwise fall back to prop downloads or default
  const downloadFiles = useMemo(() => {
    if (apiDownloadFiles.length > 0) {
      return apiDownloadFiles;
    }
    if (downloads.length > 0) {
      return downloads;
    }
    return DEFAULT_DOWNLOAD_FILES;
  }, [apiDownloadFiles, downloads]);
  
  const {
    uploadedFiles: hookUploadedFiles,
    uploadingIds,
    handleFileUpload,
  } = useSupportingDocuments(regId, onSubmitSuccess);

  // Merge uploaded status from files prop with hook's uploadedFiles state
  const uploadedFiles = useMemo(() => {
    const merged = { ...hookUploadedFiles };
    
    // Add upload status from files prop (from database) - include all uploaded files regardless of active status
    if (files && Array.isArray(files)) {
      files.forEach((file) => {
        if (file.uploaded === 1) {
          merged[file.id] = {
            uploaded: true,
            filePath: file.file_path,
            fileName: file.file_name || (file.file_path ? file.file_path.split('/').pop() : null),
          };
        }
      });
    }
    
    return merged;
  }, [hookUploadedFiles, files]);

  return (
    <SupportingDocumentsContainer>
      <UploadSection
        uploadFiles={uploadFiles}
        uploadedFiles={uploadedFiles}
        uploadingIds={uploadingIds}
        setReloadFiles={setReloadFiles}
        onUpload={handleFileUpload}
        depositAmount={depositAmount}
      />
      
      <DownloadSection downloadFiles={downloadFiles} />
      
      <PaymentSection 
        studentNumber={studentNumber} 
        showPaymentMethods={showPaymentMethods}
      />
    </SupportingDocumentsContainer>
  );
}

export default React.memo(SupportingDocuments);

